/* Replacement Finder v4 — tag-driven dynamic matcher
 *
 * On system select:
 *   - Read data-app + data-stage + data-gpds + data-inline from the selected <option>
 *   - Find kits where: kit.applications.includes(systemApp)
 *       Default: prefer full_replacement kits; if none exist, fall back to pre_filter_only kits.
 *       Special case: systems with data-inline=1 (e.g. MPDW) show BOTH full_replacement
 *       AND pre_filter_only kits for their app, because the system uses a different filter
 *       form-factor (inline post-carbon) and pre-filter-only kits are still valid replacements.
 *   - Within the candidate pool: prefer exact filter_count match (count == stage-1).
 *     If no exact match, surface the closest available filter_count.
 *   - Sort by price descending (premium-first upsell), show top 3 + "See all" expander.
 *   - Below: cartridges
 *       universal scope -> always shown (sediment + 10" carbon)
 *       inline scope    -> only when usesInline (post-carbon for MPDW etc.)
 *       rodi-only scope -> only when app == 'RODI'
 *   - Below: matching membrane(s) by GPD. Exact match preferred, closest as fallback.
 */
(function () {
  'use strict';

  document.querySelectorAll('[data-replacement-finder]').forEach(initFinder);

  function initFinder(root) {
    const sysSelect = root.querySelector('[data-system]');
    const resultsEl = root.querySelector('[data-results]');
    const fallbackEl = root.querySelector('[data-fallback]');
    const kitCatalog = parseJSON(root.querySelector('[data-kit-catalog]'));
    const cartCatalog = parseJSON(root.querySelector('[data-cartridge-catalog]'));
    const membCatalog = parseJSON(root.querySelector('[data-membrane-catalog]'));

    if (!sysSelect || !resultsEl || !kitCatalog) return;

    sysSelect.addEventListener('change', () => {
      const opt = sysSelect.options[sysSelect.selectedIndex];
      const app = (opt.dataset.app || '').toUpperCase();
      const stage = parseInt(opt.dataset.stage, 10);
      const gpds = (opt.dataset.gpds || '')
        .split(',')
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !isNaN(n));
      const usesInline = opt.dataset.inline === '1';

      if (!app || !stage) {
        resultsEl.hidden = true;
        if (fallbackEl) fallbackEl.hidden = true;
        return;
      }

      render({ app, stage, gpds, usesInline });
    });

    function render({ app, stage, gpds, usesInline }) {
      const targetCount = stage - 1;

      // Kits that include this app in their applications array.
      const appKits = kitCatalog.filter(k =>
        Array.isArray(k.applications) && k.applications.includes(app)
      );

      let candidates;
      let kitMode; // 'full' | 'pre' | 'mixed'
      if (usesInline) {
        // Inline-carbon systems (MPDW etc.) have very specific kit requirements:
        // restrict to kits explicitly tagged KIT-FOR-INLINE-SYSTEMS for this app.
        // (Generic same-app kits like the DWS 3-cartridge kit don't fit MPDW's
        // 2-cartridge + membrane + inline-post stack.)
        candidates = appKits.filter(k => k.for_inline === true);
        kitMode = 'mixed';
      } else {
        // Non-inline systems: exclude KIT-FOR-INLINE-SYSTEMS *full-replacement* kits
        // (system-specific kits like the MPDW kit shouldn't show for DWS). However,
        // pre-filter kits tagged KIT-FOR-INLINE-SYSTEMS (the 4 Eliminator pre-filter
        // kits) ARE compatible with non-inline systems via their HYDRO-KIT scope.
        const nonSystemSpecific = appKits.filter(k =>
          !(k.for_inline === true && k.role === 'full_replacement')
        );
        const fullKits = nonSystemSpecific.filter(k => k.role === 'full_replacement');
        const preKits = nonSystemSpecific.filter(k => k.role === 'pre_filter_only');
        if (fullKits.length > 0) {
          candidates = fullKits;
          kitMode = 'full';
        } else {
          candidates = preKits;
          kitMode = 'pre';
        }
      }

      // Match by filter_count.
      //   mixed mode (inline-carbon systems like MPDW): show ALL app-scoped kits — both
      //     full replacements and pre-filter alternates — regardless of filter_count,
      //     because the system uses a mixed form-factor (inline post-carbon) and stage-count
      //     math doesn't map cleanly to cartridge-count.
      //   full mode: prefer exact filter_count, otherwise closest.
      //   pre mode:  same logic, on pre-filter pool only.
      let matched;
      let isClosestMatch = false;
      if (kitMode === 'mixed') {
        matched = candidates.slice();
      } else {
        const exact = candidates.filter(k => k.filter_count === targetCount);
        let closest = [];
        if (exact.length === 0) {
          const allCounts = candidates.map(k => k.filter_count).filter(n => n !== null);
          if (allCounts.length) {
            const closestCount = allCounts.reduce((best, c) =>
              Math.abs(c - targetCount) < Math.abs(best - targetCount) ? c : best,
              allCounts[0]
            );
            closest = candidates.filter(k => k.filter_count === closestCount);
          }
        }
        matched = exact.length ? exact : closest;
        isClosestMatch = exact.length === 0 && closest.length > 0;
      }

      const isPreFilterFallback = kitMode === 'pre';

      matched.sort((a, b) => b.price_value - a.price_value);

      // Cartridges: scope rules
      const cartridges = (cartCatalog || []).filter(c => {
        if (c.scope === 'universal') return true;
        if (c.scope === 'inline') return usesInline;
        if (c.scope === 'rodi-only') return app === 'RODI';
        return false;
      });
      cartridges.sort((a, b) => b.price_value - a.price_value);

      // Membranes: match any GPD in gpds[] exact, closest as fallback.
      let membranes = [];
      if (membCatalog && gpds.length) {
        membranes = (membCatalog || []).filter(m => gpds.includes(m.gpd));
        if (membranes.length === 0) {
          let bestMem = null;
          let bestDist = Infinity;
          (membCatalog || []).forEach(m => {
            gpds.forEach(sg => {
              const d = Math.abs(m.gpd - sg);
              if (d < bestDist) { bestDist = d; bestMem = m.gpd; }
            });
          });
          if (bestMem !== null) {
            membranes = (membCatalog || []).filter(m => m.gpd === bestMem);
          }
        }
      }
      membranes.sort((a, b) => b.price_value - a.price_value);

      const html = buildHTML({
        matched, cartridges, membranes,
        app, stage, targetCount,
        isClosestMatch, isPreFilterFallback, kitMode,
        usesInline, gpds
      });
      resultsEl.innerHTML = html;
      resultsEl.hidden = false;
      if (fallbackEl) fallbackEl.hidden = false;

      resultsEl.querySelectorAll('[data-expand]').forEach(btn => {
        btn.addEventListener('click', () => {
          const target = resultsEl.querySelector(btn.dataset.expand);
          if (target) target.hidden = false;
          btn.remove();
        });
      });
    }

    function buildHTML({ matched, cartridges, membranes, app, stage, targetCount, isClosestMatch, isPreFilterFallback, kitMode }) {
      const appLabel = { RO: 'RO', RODI: 'RO/DI', DRINK: 'Drinking Water', COFFEE: 'Coffee', HYDRO: 'Hydroponics' }[app] || app;

      let h = '';

      // Header
      h += `<div class="c-finder__summary" style="margin-bottom:var(--sp-16);padding:var(--sp-12) var(--sp-16);background:var(--color-surface);border-radius:8px;font-size:var(--fs-14);color:var(--color-text-secondary);">`;
      h += `Matching <strong>${appLabel}</strong> parts for your <strong>${stage}-stage system</strong> (${targetCount} cartridges).`;
      if (isPreFilterFallback) {
        h += ` <em>Showing pre-filter kits and individual cartridges — no full-stage kit configured for this system.</em>`;
      } else if (kitMode === 'mixed') {
        h += ` <em>Showing the matching kit plus pre-filter alternates compatible with this system.</em>`;
      } else if (isClosestMatch) {
        h += ` <em>No exact stage match — showing closest available kit.</em>`;
      }
      h += `</div>`;

      // Kits
      if (matched.length === 0) {
        h += `<div class="c-empty" style="padding:var(--sp-20);text-align:center;border:1px dashed var(--color-border);border-radius:12px;">`;
        h += `<p style="margin:0;color:var(--color-text-secondary);">No standard kit configured for this system yet. Use the cartridges below or contact support.</p>`;
        h += `</div>`;
      } else {
        const kitHeading = (kitMode === 'pre') ? 'Pre-filter kits' : (kitMode === 'mixed' ? 'Replacement kits & pre-filter options' : 'Replacement kits');
        h += `<h3 style="margin:0 0 var(--sp-12) 0;font-size:var(--fs-18);font-weight:var(--font-weight-semibold);">${kitHeading}</h3>`;
        const topThree = matched.slice(0, 3);
        const rest = matched.slice(3);

        h += `<div class="c-finder__kit-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:var(--sp-12);">`;
        topThree.forEach(k => { h += kitCard(k); });
        h += `</div>`;

        if (rest.length > 0) {
          h += `<div id="rf-more-kits" hidden style="margin-top:var(--sp-12);">`;
          h += `<div class="c-finder__kit-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:var(--sp-12);">`;
          rest.forEach(k => { h += kitCard(k); });
          h += `</div></div>`;
          h += `<button type="button" class="c-button c-button--ghost" data-expand="#rf-more-kits" style="margin-top:var(--sp-12);">See all ${matched.length} compatible kits</button>`;
        }
      }

      // Membranes
      if (membranes.length > 0) {
        h += `<div style="margin-top:var(--sp-24);padding-top:var(--sp-20);border-top:1px solid var(--color-border);">`;
        h += `<h3 style="margin:0 0 var(--sp-8) 0;font-size:var(--fs-18);font-weight:var(--font-weight-semibold);">RO membrane</h3>`;
        h += `<p style="margin:0 0 var(--sp-16) 0;color:var(--color-text-secondary);font-size:var(--fs-14);">Replace your membrane every 2–3 years for best rejection.</p>`;
        const topM = membranes.slice(0, 3);
        const restM = membranes.slice(3);
        h += `<div class="c-finder__kit-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:var(--sp-12);">`;
        topM.forEach(m => { h += kitCard(m); });
        h += `</div>`;
        if (restM.length > 0) {
          h += `<div id="rf-more-mem" hidden style="margin-top:var(--sp-8);">`;
          h += `<div class="c-finder__kit-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:var(--sp-12);">`;
          restM.forEach(m => { h += kitCard(m); });
          h += `</div></div>`;
          h += `<button type="button" class="c-button c-button--ghost c-button--sm" data-expand="#rf-more-mem" style="margin-top:var(--sp-8);">Show ${restM.length} more membrane options</button>`;
        }
        h += `</div>`;
      }

      // Cartridges
      if (cartridges.length > 0) {
        const sediment = cartridges.filter(c => c.type === 'sediment');
        const carbonUniv = cartridges.filter(c => c.type === 'carbon' && c.scope === 'universal');
        const carbonInline = cartridges.filter(c => c.type === 'carbon' && c.scope === 'inline');
        const di = cartridges.filter(c => c.type === 'di');

        h += `<div style="margin-top:var(--sp-24);padding-top:var(--sp-20);border-top:1px solid var(--color-border);">`;
        h += `<h3 style="margin:0 0 var(--sp-8) 0;font-size:var(--fs-18);font-weight:var(--font-weight-semibold);">Individual cartridges</h3>`;
        h += `<p style="margin:0 0 var(--sp-16) 0;color:var(--color-text-secondary);font-size:var(--fs-14);">Replace one stage at a time — these fit your system's housings.</p>`;

        h += cartridgeGroup('Sediment', sediment, 'rf-more-sediment', 3);
        h += cartridgeGroup('Carbon (10" housing)', carbonUniv, 'rf-more-carbon', 3);
        if (carbonInline.length) {
          h += cartridgeGroup('Inline carbon (post-filter)', carbonInline, 'rf-more-inline', 3);
        }
        if (app === 'RODI') h += cartridgeGroup('DI Resin', di, 'rf-more-di', 3);
        h += `</div>`;
      }

      return h;
    }

    function kitCard(k) {
      return `
        <a href="${k.url}" class="c-finder__card" style="display:block;padding:var(--sp-12);border:1px solid var(--color-border);border-radius:10px;text-decoration:none;color:inherit;background:var(--color-bg);transition:border-color .15s;" onmouseover="this.style.borderColor='var(--color-accent)'" onmouseout="this.style.borderColor='var(--color-border)'">
          ${k.image ? `<img src="${k.image}" alt="${escapeHTML(k.title)}" style="width:100%;height:140px;object-fit:contain;background:var(--color-surface);border-radius:6px;margin-bottom:var(--sp-8);" loading="lazy">` : ''}
          <div style="font-size:var(--fs-14);font-weight:var(--font-weight-medium);line-height:1.3;margin-bottom:var(--sp-4);">${escapeHTML(k.title)}</div>
          <div style="font-size:var(--fs-14);color:var(--color-text-secondary);">${k.price}</div>
        </a>`;
    }

    function cartridgeGroup(label, items, moreId, topN) {
      if (!items.length) return '';
      const top = items.slice(0, topN);
      const rest = items.slice(topN);
      let h = `<div style="margin-bottom:var(--sp-16);">`;
      h += `<h4 style="margin:0 0 var(--sp-8) 0;font-size:var(--fs-14);font-weight:var(--font-weight-semibold);text-transform:uppercase;letter-spacing:0.5px;color:var(--color-text-secondary);">${label}</h4>`;
      h += `<div class="c-finder__cart-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:var(--sp-8);">`;
      top.forEach(c => { h += kitCard(c); });
      h += `</div>`;
      if (rest.length > 0) {
        h += `<div id="${moreId}" hidden style="margin-top:var(--sp-8);">`;
        h += `<div class="c-finder__cart-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:var(--sp-8);">`;
        rest.forEach(c => { h += kitCard(c); });
        h += `</div></div>`;
        h += `<button type="button" class="c-button c-button--ghost c-button--sm" data-expand="#${moreId}" style="margin-top:var(--sp-8);">Show ${rest.length} more ${label.toLowerCase()}</button>`;
      }
      h += `</div>`;
      return h;
    }

    function escapeHTML(s) {
      return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
    }

    function parseJSON(el) {
      if (!el) return null;
      try { return JSON.parse(el.textContent); }
      catch (e) { console.warn('replacement-finder: failed to parse', e); return null; }
    }
  }
})();
